import { ReactNode, useMemo } from "react";
import { CssBaseline, Theme, ThemeProvider } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { useColorMode } from "~/providers/color-mode";
import { createMondooTheme } from "~/themes/createTheme";
import { SnackbarComponents } from "~/themes/notification";
import { darkTheme } from "~/themes";
import "~/style/index.css";
import { CustomTransition } from "~/components/Snackbar/CustomTransition";
import { createFoundationTheme } from "~/themes/createFoundationTheme";
import { useFeatureFlags } from "./FeatureFlagsProvider";

type MondooThemeProviderProps = {
  children: ReactNode;
  loggedIn?: boolean;
  defaultTheme?: Theme;
};

export function MondooThemeProvider({
  children,
  loggedIn = false,
  defaultTheme,
}: MondooThemeProviderProps) {
  // when not logged in, we don't have access to a users preference, so we return dark mode by default
  let theme;
  const { isFeatureEnabled } = useFeatureFlags();
  if (loggedIn) {
    const { mode, colorBlind, getThemeFromProvider } = useColorMode();
    const isFoundationThemesEnabled = isFeatureEnabled("Foundation themes");

    theme = useMemo(
      () =>
        isFoundationThemesEnabled
          ? createFoundationTheme(getThemeFromProvider())
          : createMondooTheme(getThemeFromProvider()),
      [mode, colorBlind, isFoundationThemesEnabled],
    );
  } else {
    theme = createMondooTheme(darkTheme);
  }

  return (
    <ThemeProvider theme={defaultTheme ? defaultTheme : theme}>
      <CssBaseline enableColorScheme />
      <SnackbarProvider
        maxSnack={3}
        Components={SnackbarComponents}
        TransitionComponent={CustomTransition}
      >
        {children}
      </SnackbarProvider>
    </ThemeProvider>
  );
}
